var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Button, Page, Tooltip } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useForm, useFormContext, useWatch, } from 'react-hook-form';
import { integrationsApi, } from 'apis/integrations';
import { analytics } from 'app/utils/segment';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { isResponseWithData } from 'utils/apis/axios';
import { articles } from 'utils/elevio';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { SettingsIntegrationsDocumentationButton } from '../../../../../../components/documentationButton/documentationButton';
import { cardV2 } from '../../../../cards.types';
import { CustomCardsForm } from '../../components/form/settingsIntegrationsCardsCustomForm';
import { getAuthorizationHeaderType } from '../../utils/getAuthorizationType/getAuthorizationType';
/** Create custom cards page. */
export const SettingsIntegrationsCardsCustomCreateView = () => {
    const navigate = useAppNavigate();
    const notify = useNotifications();
    const [createCustomCard, { isLoading }] = integrationsApi.useCreateCustomCardIntegrationMutation();
    const { control: oAuthFormControl, formState: { isDirty: isOAuthFormDirty }, } = useFormContext();
    const [requestOAuthId] = useWatch({
        control: oAuthFormControl,
        name: [
            'request.o_auth_connection_id',
        ],
    });
    const hasOAuthConnectionId = !!requestOAuthId;
    // We disable the custom card save button when we don't have a connection id
    // yet, but the OAuth form is dirty.
    const disableCustomCardSaveButton = !hasOAuthConnectionId && isOAuthFormDirty;
    const customCardForm = useForm({
        mode: 'onTouched',
        defaultValues: {
            request: {
                http_method: ['GET'],
            },
        },
    });
    const onSubmit = (formValues) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const uriTemplate = (_a = formValues === null || formValues === void 0 ? void 0 : formValues.request) === null || _a === void 0 ? void 0 : _a.uri_template;
        const hasUriTemplate = !!uriTemplate && uriTemplate !== '';
        const basicFormValues = {
            title: formValues.title,
            template: formValues.template,
            card_version: cardV2,
            integration_type: 'custom',
        };
        const authorizationHeader = formValues === null || formValues === void 0 ? void 0 : formValues.authorizationHeader;
        const customHeaders = formValues === null || formValues === void 0 ? void 0 : formValues.customHeaders;
        const requestFormValues = hasUriTemplate
            ? {
                request: {
                    uri_template: uriTemplate,
                    headers: Object.assign(Object.assign({}, (!!customHeaders &&
                        Object.keys(customHeaders).length > 0 && Object.assign({}, customHeaders))), (!!authorizationHeader && {
                        authorization: authorizationHeader,
                    })),
                    body: !!((_b = formValues.request) === null || _b === void 0 ? void 0 : _b.body)
                        ? formValues.request.body
                        : undefined,
                    http_method: formValues.request.http_method[0],
                    content_type: ((_d = (_c = formValues.request) === null || _c === void 0 ? void 0 : _c.content_type) === null || _d === void 0 ? void 0 : _d[0]) || undefined,
                    o_auth_connection_id: (_e = formValues === null || formValues === void 0 ? void 0 : formValues.request) === null || _e === void 0 ? void 0 : _e.o_auth_connection_id,
                },
            }
            : {};
        const response = yield createCustomCard(Object.assign(Object.assign({}, basicFormValues), requestFormValues));
        if (isResponseWithData(response)) {
            notify({
                title: 'Success',
                text: 'Custom card has been created.',
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            if (hasUriTemplate) {
                const authorizationHeaderType = getAuthorizationHeaderType(authorizationHeader) ||
                    (((_f = formValues === null || formValues === void 0 ? void 0 : formValues.request) === null || _f === void 0 ? void 0 : _f.o_auth_connection_id) && 'oauth') ||
                    undefined;
                analytics.settingsIntegrationsCustomCardCreated({
                    customHeaders: customHeaders ? Object.keys(customHeaders).length : 0,
                    authorizationHeader: authorizationHeaderType,
                    oAuthStrategy: formValues === null || formValues === void 0 ? void 0 : formValues.oAuthStrategy,
                    httpMethod: formValues.request.http_method[0],
                    contentType: ((_h = (_g = formValues === null || formValues === void 0 ? void 0 : formValues.request) === null || _g === void 0 ? void 0 : _g.content_type) === null || _h === void 0 ? void 0 : _h[0]) || undefined,
                    body: !!((_j = formValues.request) === null || _j === void 0 ? void 0 : _j.body),
                });
            }
            else {
                analytics.settingsIntegrationsCustomCardCreated();
            }
            const { data } = response;
            navigate({
                path: '/settings/integrations/custom-card/edit/:id',
                params: { path: { id: data.data.id } },
            });
        }
        else {
            notify({
                title: 'Error while creating the custom card.',
            }, NotificationType.Error);
        }
    });
    return (_jsx(Page.Section, Object.assign({ title: "Create custom card", titleMeta: _jsx(SettingsIntegrationsDocumentationButton, { onClick: () => elevioClient.openArticle(articles.settings.cards.custom) }), controls: _jsx(Tooltip, Object.assign({ dark: true, content: disableCustomCardSaveButton
                ? 'The OAuth configuration needs to be saved before the card is saved'
                : undefined }, { children: _jsx(Button, Object.assign({ isLoading: isLoading, disabled: disableCustomCardSaveButton, onClick: customCardForm.handleSubmit(onSubmit) }, { children: "Save changes" })) })) }, { children: _jsx(CustomCardsForm, { control: customCardForm.control, handleCustomCardSubmit: customCardForm.handleSubmit(onSubmit) }) })));
};
